'use client'

import type { ChainMetadata, TokenKeyWithMetadata } from '@polaris/core/models'
import { shorten } from '@polaris/core/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import { Check, Copy, Info } from 'lucide-react'
import { useCallback } from 'react'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import { cn } from '@/src/utils/styles'
import { Button } from '../../ui/button'
import { ChainImg } from '../../ui/chain-img'
import { Chevron } from '../../ui/icons/chevron'
import {
  ListRowButton,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../../ui/list-row'
import {
  Tooltip,
  TooltipContent,
  TooltipDescription,
  TooltipProvider,
  TooltipTitle,
  TooltipTrigger,
} from '../../ui/tooltip'

interface ContractAddressDropdownProps {
  chains: (ChainMetadata & TokenKeyWithMetadata)[]
  onClick?: (data: { denom: string; chain_id: string }) => void
}

export default function ContractAddressDropdown({
  chains,
  onClick: onClickCallback,
}: ContractAddressDropdownProps) {
  const {
    isOpen: isContractAddressesDropdownOpen,
    close,
    onToggle,
  } = useDisclosure()

  const {
    isOpen: isCopyConfirmationOpen,
    open: openConfirmation,
    close: closeConfirmation,
  } = useDisclosure()

  const copy = useCallback(
    async (data: string) => {
      await navigator.clipboard.writeText(data)
      close()

      openConfirmation()
      setTimeout(closeConfirmation, 2000)
    },
    [close, closeConfirmation, openConfirmation],
  )

  const onClick = useCallback(
    async (data: { chain_id: string; denom: string }) => {
      await copy(data.denom)
      onClickCallback?.(data)
    },
    [copy, onClickCallback],
  )

  return (
    <TooltipProvider>
      <Tooltip open={isCopyConfirmationOpen}>
        <TooltipTrigger asChild>
          <div className='absolute w-[180px] h-8 pointer-events-none' />
        </TooltipTrigger>
        <TooltipContent side='right' hideArrow>
          <div className='flex items-center gap-3'>
            <Check
              width={16}
              height={16}
              className='size-4 text-success-text'
            />
            <TooltipTitle>Copied to clipboard</TooltipTitle>
          </div>
        </TooltipContent>
      </Tooltip>
      <DropdownMenu
        open={isContractAddressesDropdownOpen}
        onOpenChange={onToggle}
      >
        <Tooltip delayDuration={150}>
          <DropdownMenuTrigger asChild>
            {/** This div is useful for not conflicting with HTML rules on not having a <button /> as descendant of a <button /> */}
            <div>
              <TooltipTrigger asChild>
                <Button
                  className='flex items-center gap-2 py-1.5 px-3 rounded-[32px]'
                  variant='secondary'
                  type='button'
                  size='sm'
                  onClick={onToggle}
                >
                  {chains?.[0] ? (
                    <ChainImg
                      chainId={chains[0].chain_id}
                      chainImg={chains[0].chain_img}
                      chainName={chains[0].chain_name}
                      width={12}
                      height={12}
                      containerClassName='size-4'
                      className='size-2'
                    />
                  ) : (
                    <div className='flex items-center justify-center min-w-8  px-1 bg-surface1 rounded-[6px]'>
                      <span className='text-secondary uppercase'>
                        {chains[0].chain_name.slice(0, 3)}
                      </span>
                    </div>
                  )}
                  <span className='text-sm'>
                    {chains[0].is_native
                      ? 'Native token'
                      : shorten(chains[0].denom)}
                  </span>
                  <Chevron
                    width={16}
                    height={16}
                    className='text-button-secondary-text'
                  />
                </Button>
              </TooltipTrigger>
            </div>
          </DropdownMenuTrigger>
          <TooltipContent
            side='right'
            hideArrow
            className={cn({
              hidden: isContractAddressesDropdownOpen,
            })}
          >
            <TooltipTitle>
              <span>{chains[0].chain_name}</span> token address
            </TooltipTitle>
            <TooltipDescription className='font-mono font-normal break-words'>
              {chains[0].denom}
            </TooltipDescription>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuPortal>
          <DropdownMenuContent className='flex flex-col w-64 max-h-72 relative overflow-auto no-scrollbar p-2 my-2 data-[state=open]:animate-enter-scale data-[side=top]:origin-bottom data-[side=bottom]:origin-top data-[state=closed]:animate-exit-scale bg-foreground border border-border1 bg-clip-padding shadow-2xl rounded-2xl'>
            <div className='flex justify-between items-center px-3 py-1.5'>
              <span className='text-secondary text-sm'>Token addresses</span>
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='text-tertiary'
                    squared
                  >
                    <Info size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  className='max-w-[280px]'
                  side='right'
                  hideArrow
                >
                  <div className='flex flex-col gap-1 w-full'>
                    <TooltipTitle>What is a token address?</TooltipTitle>
                    <TooltipDescription>
                      A token or contract address is the unique identifier for a
                      token or smart contract on a blockchain, acting as its
                      “home address” for interactions.
                      <br />
                      <br /> Always verify these addresses with a third-party to
                      ensure you’re interacting with the desired token and avoid
                      scams or counterfeit tokens.
                    </TooltipDescription>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className='flex flex-col pb-2'>
              {chains.map((address, i) => (
                <ContractAddress
                  key={i}
                  {...address}
                  onClick={onClick}
                  disabled={address.is_native}
                />
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </TooltipProvider>
  )
}

const ContractAddress = ({
  chain_name,
  denom,
  chain_id,
  chain_img,
  is_native,
  disabled,
  onClick,
}: (ChainMetadata & TokenKeyWithMetadata) & {
  disabled?: boolean
  onClick: (data: { chain_id: string; denom: string }) => Promise<void>
}) => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild disabled={disabled}>
      <ListRowButton
        onClick={() =>
          onClick({
            chain_id,
            denom,
          })
        }
        size='sm'
      >
        <div className='flex items-center gap-3'>
          {chain_img ? (
            <ChainImg
              chainId={chain_id}
              chainImg={chain_img}
              chainName={chain_name}
              width={20}
              height={20}
              containerClassName='min-w-8 h-8'
              className='size-5'
            />
          ) : (
            <div className='flex items-center justify-center min-w-8 size-8 bg-surface1 rounded-[6px]'>
              <span className='text-secondary'>{chain_name.slice(0, 3)}</span>
            </div>
          )}
          <ListRowSide>
            <ListRowSideName className='leading-5'>
              {chain_name.split('-').join(' ')}
            </ListRowSideName>
            <ListRowSideDescription className='whitespace-nowrap text-xs'>
              {is_native ? 'Native token' : shorten(denom)}
            </ListRowSideDescription>
          </ListRowSide>
        </div>
        {!disabled && (
          <div className='flex items-center justify-center size-6 min-w-6'>
            <Copy
              width={16}
              height={16}
              className='text-tertiary min-w-4 transition-colors duration-200 group-hover:text-primary'
              strokeWidth={3}
            />
          </div>
        )}
      </ListRowButton>
    </TooltipTrigger>
    <TooltipContent className='max-w-[280px]' side='right' hideArrow>
      <div className='flex flex-col gap-1 w-full'>
        <TooltipTitle>Token address</TooltipTitle>
        <TooltipDescription className='font-mono font-normal break-words'>
          {denom}
        </TooltipDescription>
      </div>
    </TooltipContent>
  </Tooltip>
)
